(function () {
  var WORD_STAGGER = 95;
  var WORD_DURATION = 820;
  var LINE_GAP = 220;
  var HOLD_AFTER_TYPE = 1500;
  var FADE_DURATION = 700;
  // Autoplay can be blocked outright (iOS Low Power Mode, data saver). Give up after
  // this many failed attempts and leave the headline up instead of retrying forever.
  var MAX_PLAY_FAILURES = 3;

  function wait(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function saveData() {
    return !!(navigator.connection && navigator.connection.saveData);
  }

  function parseConfig(banner) {
    return {
      lines: [
        {
          parts: [
            { text: banner.dataset.line1Before || 'The head unit is ', className: '' },
            { text: banner.dataset.line1Highlight || 'dead.', className: 'hero-accent-light' }
          ]
        },
        {
          parts: [
            { text: banner.dataset.line2Before || 'Meet the ', className: '' },
            { text: banner.dataset.line2Highlight || 'TUNE MFD.', className: 'hero-accent' }
          ]
        }
      ]
    };
  }

  function revealLines(typewriterEl, lines, cursor) {
    typewriterEl.innerHTML = '';
    typewriterEl.setAttribute('data-typed', '');

    if (cursor) {
      cursor.classList.add('is-hidden');
    }

    var wordIndex = 0;
    var lastDelay = 0;

    lines.forEach(function (line, lineIndex) {
      if (lineIndex > 0) {
        // Lines are display:block, but the H1's text still needs a space between them.
        typewriterEl.appendChild(document.createTextNode(' '));
      }
      var lineEl = document.createElement('span');
      lineEl.className = 'hero-text-line';
      typewriterEl.appendChild(lineEl);

      var tokens = [];
      line.parts.forEach(function (part) {
        part.text.trim().split(/\s+/).forEach(function (word) {
          if (word) {
            tokens.push({ text: word, className: part.className });
          }
        });
      });

      tokens.forEach(function (token, tokenIndex) {
        if (tokenIndex > 0) {
          lineEl.appendChild(document.createTextNode(' '));
        }

        var wordEl = document.createElement('span');
        wordEl.className = 'hero-word' + (token.className ? ' ' + token.className : '');
        var delay = wordIndex * WORD_STAGGER + lineIndex * LINE_GAP;
        wordEl.style.animationDelay = delay + 'ms';
        wordEl.textContent = token.text;
        lineEl.appendChild(wordEl);

        lastDelay = delay;
        wordIndex += 1;
      });
    });

    return wait(lastDelay + WORD_DURATION + 80);
  }

  function setActionsFocusable(statsEl, focusable) {
    statsEl.querySelectorAll('a, button').forEach(function (el) {
      if (focusable) {
        el.removeAttribute('tabindex');
      } else {
        el.setAttribute('tabindex', '-1');
      }
    });
  }

  function showHeroButtons(banner) {
    var statsEl = banner.querySelector('[data-hero-stats]');
    if (!statsEl) {
      return;
    }

    statsEl.classList.add('is-active');
    statsEl.setAttribute('aria-hidden', 'false');
    setActionsFocusable(statsEl, true);
    watchHeroStatsVisibility(banner, statsEl);
  }

  function watchHeroStatsVisibility(banner, statsEl) {
    if (statsEl.dataset.heroStatsWatchReady === 'true') {
      return;
    }
    statsEl.dataset.heroStatsWatchReady = 'true';

    if (!('IntersectionObserver' in window)) {
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var away = !entry.isIntersecting;
        statsEl.classList.toggle('is-scrolled-away', away);
        setActionsFocusable(statsEl, !away);
      });
    }, { threshold: 0 });

    observer.observe(banner);
  }

  function prepareHeroVideo(video) {
    if (!video || video.dataset.heroVideoPrepared === 'true') {
      return;
    }

    video.dataset.heroVideoPrepared = 'true';
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.preload = 'auto';

    try {
      video.load();
    } catch (err) {
      /* ignore */
    }
  }

  function waitForCanPlay(video, timeoutMs) {
    if (!video) {
      return Promise.resolve(false);
    }

    if (video.readyState >= 3) {
      return Promise.resolve(true);
    }

    return new Promise(function (resolve) {
      var settled = false;

      function done(ok) {
        if (settled) {
          return;
        }
        settled = true;
        video.removeEventListener('canplay', onReady);
        video.removeEventListener('canplaythrough', onReady);
        video.removeEventListener('loadeddata', onReady);
        video.removeEventListener('error', onError);
        resolve(ok);
      }

      function onReady() {
        done(true);
      }

      function onError() {
        done(false);
      }

      video.addEventListener('canplay', onReady);
      video.addEventListener('canplaythrough', onReady);
      video.addEventListener('loadeddata', onReady);
      video.addEventListener('error', onError);

      window.setTimeout(function () {
        done(video.readyState >= 2);
      }, timeoutMs || 8000);
    });
  }

  function playHeroVideo(video) {
    if (!video) {
      return Promise.resolve(false);
    }

    prepareHeroVideo(video);

    return waitForCanPlay(video, 8000).then(function (ready) {
      if (!ready && video.readyState < 2) {
        return false;
      }

      var attempt = function (remaining) {
        var playPromise = video.play();
        if (!playPromise || typeof playPromise.then !== 'function') {
          return Promise.resolve(!video.paused);
        }

        return playPromise.then(function () {
          return true;
        }).catch(function () {
          if (remaining <= 0) {
            return false;
          }
          return wait(350).then(function () {
            return attempt(remaining - 1);
          });
        });
      };

      return attempt(3);
    });
  }

  // Fade the headline away. Only called once the video is playing underneath, so the
  // text dissolves straight into the footage with no empty frame in between.
  function hideIntro(intro) {
    if (!intro || intro.classList.contains('is-hidden')) {
      return Promise.resolve();
    }

    intro.classList.add('is-fading');
    return wait(FADE_DURATION).then(function () {
      intro.classList.add('is-hidden');
      intro.classList.remove('is-fading');
    });
  }

  function waitForVideoEnd(video) {
    if (!video) {
      return Promise.resolve();
    }

    return new Promise(function (resolve) {
      var settled = false;
      var safetyMs = 20000;
      var timer;

      function done() {
        if (settled) {
          return;
        }
        settled = true;
        video.removeEventListener('ended', done);
        video.removeEventListener('error', done);
        window.clearTimeout(timer);
        resolve();
      }

      if (video.duration && isFinite(video.duration)) {
        safetyMs = Math.max(4000, (video.duration * 1000) + 1500);
      }

      video.addEventListener('ended', done);
      video.addEventListener('error', done);
      timer = window.setTimeout(done, safetyMs);
    });
  }

  function runCycle(banner) {
    var video = banner.querySelector('.hero-video');
    var intro = banner.querySelector('[data-hero-intro-screen]');
    var typewriter = banner.querySelector('[data-hero-typewriter]');
    var cursor = banner.querySelector('.hero-cursor');
    var config = parseConfig(banner);

    if (!intro || !typewriter) {
      return Promise.resolve(false);
    }

    // Reduced motion or data saver: keep the headline on screen, skip the video.
    if (prefersReducedMotion() || saveData() || !video) {
      typewriter.setAttribute('data-typed', '');
      if (cursor) {
        cursor.classList.add('is-hidden');
      }
      showHeroButtons(banner);
      return Promise.resolve(false);
    }

    prepareHeroVideo(video);
    video.loop = false;
    video.autoplay = false;
    video.pause();
    try {
      video.currentTime = 0;
    } catch (err) {
      /* ignore until metadata loads */
    }

    intro.classList.remove('is-hidden', 'is-fading');

    return revealLines(typewriter, config.lines, cursor)
      .then(function () {
        return wait(HOLD_AFTER_TYPE);
      })
      .then(function () {
        return playHeroVideo(video);
      })
      .then(function (playing) {
        showHeroButtons(banner);
        if (!playing) {
          // Autoplay blocked: keep the headline up rather than an empty hero.
          return false;
        }
        return hideIntro(intro)
          .then(function () {
            return waitForVideoEnd(video);
          })
          .then(function () {
            return true;
          });
      });
  }

  function loopVideo(banner) {
    var video = banner.querySelector('.hero-video');
    var intro = banner.querySelector('[data-hero-intro-screen]');
    var failures = 0;

    if (!video) {
      return;
    }

    function playVideo() {
      try {
        video.currentTime = 0;
      } catch (err) {
        /* ignore */
      }

      playHeroVideo(video).then(function (playing) {
        if (!playing) {
          failures += 1;
          if (failures < MAX_PLAY_FAILURES) {
            window.setTimeout(playVideo, 1200);
          }
          return;
        }

        failures = 0;
        hideIntro(intro);
        waitForVideoEnd(video).then(playVideo);
      });
    }

    // A tap or click can unlock playback that autoplay policy blocked.
    var resumeOnGesture = function () {
      if (video.paused) {
        failures = 0;
        playVideo();
      }
    };
    document.addEventListener('touchstart', resumeOnGesture, { passive: true, once: true });
    document.addEventListener('click', resumeOnGesture, { once: true });

    playVideo();
  }

  function initBanner(banner) {
    if (banner.dataset.heroIntroReady === 'true') {
      return;
    }
    banner.dataset.heroIntroReady = 'true';

    var animate = !(prefersReducedMotion() || saveData());

    runCycle(banner).then(function () {
      if (animate) {
        loopVideo(banner);
      }
    });
  }

  function init() {
    document.querySelectorAll('[data-hero-intro]').forEach(initBanner);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
